import { useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  Download,
  Loader2,
  MicOff,
  Moon,
  Pause,
  Play,
  RotateCcw,
  Square,
  Trash2,
  WifiOff,
  type LucideIcon,
} from 'lucide-react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@clarix/ui';
import { cn } from '@clarix/ui/lib/cn';
import { ConfirmDialog } from '@clarix/ui-shared';
import { CALENDAR_PATH, QM_POLL } from './constants';
import { QM_COPY } from './copy';
import { formatElapsed } from './format';
import { MicNotice } from './MicNotice';
import { MicPicker } from './MicPicker';
import { useQuickMeeting } from './QuickMeetingContext';
import { useMicQuiet, useMicSegments, type MicLevelStore } from './useMicLevel';
import { useNow } from './useNow';
import { useOnline } from './browserSignals';
import type { QuickMeetingMicValue } from './useQuickMeetingMic';
import { recordedMsAt } from './useQuickMeetingRecorder';

type PillVariant = 'bar' | 'floating';

const SHELL = 'flex h-8 shrink-0 items-center gap-1 rounded-full border pl-2.5 pr-1 text-lg';
const FLOATING = 'shadow-overlay';

const TONE = {
  recording: 'border-destructive/30 bg-destructive-light text-destructive-fg',
  warning: 'border-warning/40 bg-warning-light text-warning-fg',
  paused: 'border-border bg-muted text-accent-foreground',
  saving: 'border-border bg-surface text-foreground',
  failed: 'border-destructive/30 bg-destructive-light text-destructive-fg',
} as const;

const DOT = {
  recording: 'bg-destructive animate-pulse motion-reduce:animate-none',
  warning: 'bg-warning animate-pulse motion-reduce:animate-none',
  paused: 'bg-muted-foreground',
} as const;

/** One bar per QM_MIC_METER segment, shortest first. */
const METER_BARS = ['h-1', 'h-1.5', 'h-2', 'h-2.5'] as const;

/** Decorative: assistive tech hears the no-speech notice instead of ten updates a second. */
function MicLevelMeter({ store }: { store: MicLevelStore }) {
  const lit = useMicSegments(store);
  return (
    <span className="flex h-3 shrink-0 items-end gap-0.5" aria-hidden>
      {METER_BARS.map((height, i) => (
        <span
          key={height}
          className={cn(
            'w-0.5 rounded-full bg-current transition-opacity duration-100 motion-reduce:transition-none',
            height,
            i < lit ? 'opacity-100' : 'opacity-25',
          )}
        />
      ))}
    </span>
  );
}

interface MicWarningProps {
  store: MicLevelStore;
  inputLost: boolean;
  paused: boolean;
  variant: PillVariant;
  onOpenPicker: () => void;
}

/**
 * The bar's mic warning, a lost mic before no speech; its button opens the picker. The live
 * region stays mounted so the warning is announced when it appears.
 */
function MicWarning({ store, inputLost, paused, variant, onOpenPicker }: MicWarningProps) {
  const quiet = useMicQuiet(store) && !paused;
  const message = inputLost ? QM_COPY.micInputLost : quiet ? QM_COPY.quietTitle : '';
  const Icon = inputLost ? MicOff : AlertTriangle;
  return (
    <>
      <span className="sr-only" role="status">
        {message}
      </span>
      {message && (
        <button
          type="button"
          className="focus-visible:ring-ring flex min-w-0 items-center gap-1 rounded-full px-1 hover:underline focus-visible:outline-none focus-visible:ring-2"
          title={message}
          aria-label={`${message}. ${QM_COPY.changeMic}`}
          onClick={onOpenPicker}
        >
          <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className={cn('whitespace-nowrap', variant === 'bar' ? 'hidden xl:inline' : 'hidden sm:inline')}>
            {inputLost ? QM_COPY.micInputLostShort : QM_COPY.quietShort}
          </span>
        </button>
      )}
    </>
  );
}

/** The picker on the live recording, with the warning the bar has no room to spell out. */
function PillMicSection({ mic, store, paused }: { mic: QuickMeetingMicValue; store: MicLevelStore; paused: boolean }) {
  const quiet = useMicQuiet(store) && !paused;
  return (
    <div className="flex flex-col gap-1.5 px-2 py-1">
      <MicPicker mic={mic} />
      {mic.inputLost ? (
        <MicNotice icon={MicOff} title={QM_COPY.micInputLost} />
      ) : (
        quiet && <MicNotice icon={AlertTriangle} title={QM_COPY.quietTitle} body={QM_COPY.quietBody} />
      )}
    </div>
  );
}

interface PillNoticeProps {
  icon: LucideIcon;
  message: string;
  /** Shown where the pill has room; without it the icon alone carries the notice. */
  short?: string;
  variant: PillVariant;
}

/** A short notice inside the pill; the full sentence is its title and is announced when it appears. */
function PillNotice({ icon: Icon, message, short, variant }: PillNoticeProps) {
  return (
    <span className="flex min-w-0 items-center gap-1 px-1" title={message}>
      <span className="sr-only" role="status">
        {message}
      </span>
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {short && (
        <span className={cn('whitespace-nowrap', variant === 'bar' ? 'hidden xl:inline' : 'hidden sm:inline')} aria-hidden>
          {short}
        </span>
      )}
    </span>
  );
}

/** Discard of this tab's unsaved take: always asked first, since the audio on this device goes too. */
function DiscardOwnButton({ onDiscard }: { onDiscard: () => Promise<boolean> }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Button type="button" variant="ghost" size="xs" className="rounded-full" onClick={() => setConfirming(true)}>
        <Trash2 aria-hidden />
        {QM_COPY.discard}
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={(o) => {
          if (!o && !busy) setConfirming(false);
        }}
        title={QM_COPY.discardTitle}
        description={QM_COPY.discardBody}
        confirmLabel={QM_COPY.discardConfirm}
        variant="destructive"
        loading={busy}
        onConfirm={async () => {
          setBusy(true);
          const done = await onDiscard();
          setBusy(false);
          if (done) setConfirming(false);
        }}
      />
    </>
  );
}

/** Icon-only in the pill to save room; the label is its accessible name and tooltip. */
function DownloadOwnButton({ onDownload }: { onDownload: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className="h-6 w-6 rounded-full"
      aria-label={QM_COPY.downloadFromDevice}
      title={QM_COPY.downloadFromDevice}
      onClick={onDownload}
    >
      <Download aria-hidden />
    </Button>
  );
}

function IconAction({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <Button type="button" variant="ghost" size="icon-sm" className="h-6 w-6 rounded-full" aria-label={label} onClick={onClick}>
      {children}
    </Button>
  );
}

/** This tab's recording: timer, Pause/Resume and Stop; below `sm` the timer opens the controls. */
export function RecordingPill({ variant }: { variant: PillVariant }) {
  const qm = useQuickMeeting();
  const local = qm?.local;
  const live = local?.phase === 'recording';
  const now = useNow(QM_POLL.PILL_TICK_MS, live);
  const online = useOnline();
  const [menuOpen, setMenuOpen] = useState(false);
  if (!qm || !local) return null;
  const { phase } = local;
  const wrapper = cn(SHELL, variant === 'floating' && FLOATING);

  if (phase === 'saving' && local.uploadStuck) {
    return (
      <div className={cn(wrapper, TONE.failed)} role="alert">
        <span className="truncate font-medium" title={QM_COPY.uploadStuck}>
          {QM_COPY.uploadStuck}
        </span>
        <Button type="button" variant="ghost" size="xs" className="rounded-full" onClick={() => void qm.retrySave()}>
          <RotateCcw aria-hidden />
          {QM_COPY.retry}
        </Button>
        <DownloadOwnButton onDownload={qm.downloadOwn} />
      </div>
    );
  }

  if (phase === 'saving') {
    return (
      <div className={cn(wrapper, TONE.saving, 'pr-2.5')} role="status" aria-live="polite">
        {local.awaitingNetwork ? (
          <>
            <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate" title={QM_COPY.offlineSaving}>
              {QM_COPY.offlineSaving}
            </span>
          </>
        ) : (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            <span>{QM_COPY.saving}</span>
          </>
        )}
      </div>
    );
  }

  if (phase === 'save_failed') {
    return (
      <div className={cn(wrapper, TONE.failed)} role="alert">
        <span className="font-medium">{QM_COPY.saveFailed}</span>
        <Button type="button" variant="ghost" size="xs" className="rounded-full" onClick={() => void qm.retrySave()}>
          <RotateCcw aria-hidden />
          {QM_COPY.retry}
        </Button>
        <DownloadOwnButton onDownload={qm.downloadOwn} />
        <DiscardOwnButton onDiscard={qm.discardOwn} />
      </div>
    );
  }

  if (phase === 'no_audio') {
    return (
      <div className={cn(wrapper, TONE.failed)} role="alert">
        <span className="font-medium">{QM_COPY.noAudio}</span>
        <DiscardOwnButton onDiscard={qm.discardOwn} />
      </div>
    );
  }

  if (phase !== 'recording' && phase !== 'paused') return null;

  const paused = phase === 'paused';
  const asleep = paused && local.pauseReason === 'asleep';
  const warn = !paused && (local.warned || (local.warnAt != null && now >= local.warnAt));
  const tone = paused ? 'paused' : warn ? 'warning' : 'recording';
  const elapsed = formatElapsed(recordedMsAt(local, now));
  const timeText = paused ? `${QM_COPY.paused} ${elapsed}` : elapsed;
  const toggle = paused ? qm.resume : qm.pause;
  const toggleLabel = paused ? QM_COPY.resume : QM_COPY.pause;

  return (
    <div className={cn(wrapper, TONE[tone])} role="group" aria-label={QM_COPY.floatingLabel}>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="focus-visible:ring-ring flex items-center gap-1.5 rounded-full pr-1 focus-visible:outline-none focus-visible:ring-2"
            aria-label={`${QM_COPY.recordingLabel} ${timeText}`}
          >
            <span className={cn('h-2 w-2 shrink-0 rounded-full', DOT[tone])} aria-hidden />
            <span className="font-medium tabular-nums">{timeText}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-2" lockScroll={false}>
          <div className="flex flex-col gap-1">
            <PillMicSection mic={qm.mic} store={qm.micLevel} paused={paused} />
            <Button type="button" variant="ghost" size="sm" className="justify-start" onClick={toggle}>
              {paused ? <Play aria-hidden /> : <Pause aria-hidden />}
              {toggleLabel}
            </Button>
            <Button type="button" variant="ghost" size="sm" className="text-destructive-fg justify-start" onClick={qm.stop}>
              <Square aria-hidden />
              {QM_COPY.stop}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="justify-start"
              onClick={() => qm.openCalendarPath(CALENDAR_PATH.RECORDINGS)}
            >
              {QM_COPY.allRecordings}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {!paused && <MicLevelMeter store={qm.micLevel} />}
      {!online && <PillNotice icon={WifiOff} message={QM_COPY.offlineRecording} short={QM_COPY.offline} variant={variant} />}
      {asleep ? (
        <>
          <PillNotice icon={Moon} message={QM_COPY.asleepPaused} variant={variant} />
          <Button type="button" variant="ghost" size="xs" className="rounded-full" onClick={qm.resume}>
            <Play aria-hidden />
            {QM_COPY.resume}
          </Button>
          <Button type="button" variant="ghost" size="xs" className="rounded-full" onClick={qm.stop}>
            <Square aria-hidden />
            {QM_COPY.stopAndSave}
          </Button>
        </>
      ) : (
        <>
          <MicWarning
            store={qm.micLevel}
            inputLost={qm.mic.inputLost}
            paused={paused}
            variant={variant}
            onOpenPicker={() => setMenuOpen(true)}
          />
          <div className="hidden items-center sm:flex">
            <IconAction label={toggleLabel} onClick={toggle}>
              {paused ? <Play aria-hidden /> : <Pause aria-hidden />}
            </IconAction>
            <IconAction label={QM_COPY.stop} onClick={qm.stop}>
              <Square aria-hidden />
            </IconAction>
          </div>
        </>
      )}
    </div>
  );
}

/** What a screen reader hears when the other tab's take changes phase; the ticking time stays silent. */
const REMOTE_PHASE_ANNOUNCEMENT = {
  recording: QM_COPY.anotherTab,
  paused: `${QM_COPY.anotherTab}, ${QM_COPY.paused}`,
  saving: QM_COPY.saving,
} as const;

/** "● Recording in another tab · 12:34 · Stop" — the owning tab runs the Stop. */
export function RemoteRecordingPill({ variant }: { variant: PillVariant }) {
  const qm = useQuickMeeting();
  const remote = qm?.remote ?? null;
  const now = useNow(QM_POLL.PILL_TICK_MS, remote?.phase === 'recording');
  if (!qm || !remote) return null;
  const elapsedMs = remote.accumulatedMs + (remote.runningSince != null ? Math.max(0, now - remote.runningSince) : 0);
  const tone = remote.phase === 'paused' ? 'paused' : remote.phase === 'saving' ? 'saving' : 'recording';

  return (
    <div className={cn(SHELL, variant === 'floating' && FLOATING, TONE[tone])} role="group" aria-label={QM_COPY.anotherTab}>
      <span className="sr-only" role="status">
        {REMOTE_PHASE_ANNOUNCEMENT[tone]}
      </span>
      {tone === 'saving' ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <span className={cn('h-2 w-2 shrink-0 rounded-full', DOT[tone])} aria-hidden />
      )}
      <span className="hidden whitespace-nowrap sm:inline" aria-hidden>
        {QM_COPY.anotherTab}
      </span>
      {tone === 'saving' ? (
        <span className="font-medium tabular-nums" aria-hidden>
          {QM_COPY.saving}
        </span>
      ) : (
        <span className="font-medium tabular-nums" role="timer" aria-live="off">
          {formatElapsed(elapsedMs)}
        </span>
      )}
      {tone !== 'saving' && (
        <Button type="button" variant="ghost" size="xs" className="rounded-full" onClick={qm.stopRemote}>
          <Square aria-hidden />
          {QM_COPY.stopRemote}
        </Button>
      )}
    </div>
  );
}
